import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";

import {
  Modal,
  Text,
  Button,
  VStack,
  HStack,
  Icon,
  Card,
  userAccountPhotoShouldUseInitials,
  Shimmer,
} from "@repo/ui";
import { useTheme } from "@repo/tokens";
import { ROUTESWEB } from "@repo/utils";

import { getAccountsListApiV1UnifiedAccountsListGet } from "@oneapp/data";

import { useAuthStore, type AccountsData } from "../../store/useAuthStore";
import { NewProfileModal } from "../../pages/auth/newProfileModal";
import {
  getAccountCard,
  type UnifiedAccount,
} from "../../pages/auth/MultipleAccounts/accountCard";
import { useUnifiedAccountSelectionFlow } from "../../pages/auth/MultipleAccounts/useUnifiedAccountSelectionFlow";
import styles from "../../pages/auth/MultipleAccounts/MultipleAccounts.module.scss";
import { useIsMobile, useIsTablet } from "@repo/utils";
import modalStyles from "./ProfileModal.module.scss";
import MessageModal from "../../pages/auth/ScaLogin/Modals/Message/Message";

export type SwitchProfileModalProps = {
  isOpen: boolean;
  onClose: () => void;
};

function mapStoreAccountsToUnified(
  unified_accounts: NonNullable<AccountsData["unified_accounts"]>,
): UnifiedAccount[] {
  return unified_accounts.map((a) => ({
    unified_account_id: a.unified_account_id,
    first_name: a.first_name,
    last_name: a.last_name,
    grade: a.grade ?? 0,
    user_account_photo: a.user_account_photo ?? null,
  }));
}

export function SwitchProfileModal({
  isOpen,
  onClose,
}: SwitchProfileModalProps) {
  const navigate = useNavigate();
  const { theme } = useTheme();
  const { t: tAuth } = useTranslation("auth");
  const { t: tProfile } = useTranslation("profile");

  const isMobile = useIsMobile();
  const isTablet = useIsTablet();
  const isNarrowLayout = isMobile || isTablet;
  const STACK = isNarrowLayout ? VStack : HStack;

  const profilesData = useAuthStore((s) => s.profilesData);
  const setAccountsData = useAuthStore((s) => s.setAccountsData);
  const currentlyLoggedInAccount =
    useAuthStore().profilesData?.unified_account_details.unified_account_id;

  const [accounts, setAccounts] = useState<UnifiedAccount[]>([]);
  const [isLoadingAccounts, setIsLoadingAccounts] = useState(false);

  const uad = profilesData?.unified_account_details?.user_account_details;
  const phoneNumber = String(uad?.phone ?? "");
  const isd_code = String(uad?.isd_code ?? "+91");
  const whatsapp_consent = Boolean(uad?.whatsapp_consent ?? true);

  const defaultProfile = profilesData?.unified_account_details?.profiles?.[0];

  const getGatewayHeaders = (authorization: string) => {
    const token = authorization.replace(/^Bearer\s+/i, "").trim();
    return {
      Authorization: authorization,
      accesstoken: token,
      platform: "web",
      "product-id": import.meta.env.VITE_PRODUCT_ID ?? "300",
      "tenant-id": defaultProfile?.vertical_id ?? "1",
      "ot-tenant": "infinitylearn",
      "x-tenant": "infinitylearn",
      "x-platform": "web",
      "x-product": "1",
    };
  };

  const s = useAuthStore.getState();
  const logout = useAuthStore((s) => s.logout);
  const getProfilesBearerToken = useCallback(() => {
    return (
      s.accountsData?.access_token ??
      s.token ??
      s.transientAccessToken ??
      undefined
    );
  }, [s.accountsData?.access_token, s.token]);

  const loginMethod = useAuthStore((s) => s.loginMethod);

  const {
    proceedWithAccount,
    isProceeding,
    showContactSupportModal,
    setShowContactSupportModal,
    handleContactSupportConfirm,
  } = useUnifiedAccountSelectionFlow({
    phoneNumber,
    isd_code,
    whatsapp_consent,
    getProfilesBearerToken,
  });

  const [selectedAccount, setSelectedAccount] = useState<UnifiedAccount | null>(
    null,
  );
  const [isNewProfileModalOpen, setIsNewProfileModalOpen] = useState(false);

  useEffect(() => {
    if (!isOpen || isLoadingAccounts) return;
    const match = accounts.find(
      (a) => a.unified_account_id === currentlyLoggedInAccount,
    );
    setSelectedAccount(match ?? null);
  }, [isOpen, isLoadingAccounts, accounts, currentlyLoggedInAccount]);

  useEffect(() => {
    if (!isOpen) return;

    let cancelled = false;

    const run = async () => {
      const token = getProfilesBearerToken();
      if (!token) {
        if (!cancelled) {
          setAccounts([]);
          setIsLoadingAccounts(false);
        }
        return;
      }

      setIsLoadingAccounts(true);
      try {
        const raw = await getAccountsListApiV1UnifiedAccountsListGet(
          { args: {}, kwargs: {} },
          {
            clientName: "learnGateway",
            headers: getGatewayHeaders(`Bearer ${token}`),
          },
        );
        if (cancelled) return;

        const unified_accounts = Array.isArray(raw)
          ? (raw as AccountsData["unified_accounts"])
          : [];

        setAccounts(mapStoreAccountsToUnified(unified_accounts));

        const prev = useAuthStore.getState().accountsData;
        if (prev) {
          setAccountsData({
            ...prev,
            unified_accounts,
          });
        }
      } catch {
        if (!cancelled) {
          setAccounts([]);
        }
      } finally {
        if (!cancelled) setIsLoadingAccounts(false);
      }
    };

    void run();

    return () => {
      cancelled = true;
    };
  }, [isOpen, getProfilesBearerToken, setAccountsData]);

  const showAddAccount = accounts.length < 3;

  const handleAddProfilePress = useCallback(() => {
    setIsNewProfileModalOpen(true);
  }, []);

  const handleNewProfileContinue = useCallback(() => {
    setIsNewProfileModalOpen(false);
    onClose();
    navigate(`/${ROUTESWEB.AUTH.SIGNUP}`, {
      state: {
        phoneNumber: phoneNumber || undefined,
        isd_code,
        whatsapp_consent,
        addProfileForLoggedInUser: true,
        requireFreshTimezone: true,
      },
    });
  }, [navigate, onClose, phoneNumber, isd_code, whatsapp_consent]);

  const handleAccountPress = (account: UnifiedAccount) => {
    setSelectedAccount(account);
  };

  let noPhotoIndex = 0;
  const accountCards = accounts?.map((account) => {
    const isNoPhoto = userAccountPhotoShouldUseInitials(
      account.user_account_photo,
    );
    const colorIndex = isNoPhoto ? noPhotoIndex++ % 3 : 0;
    const isCardSelected =
      selectedAccount?.unified_account_id === account.unified_account_id;
    return getAccountCard(
      account,
      styles,
      theme,
      () => handleAccountPress(account),
      colorIndex,
      isCardSelected,
    );
  });

  const handleContinue = async () => {
    const navigated = await proceedWithAccount(selectedAccount);
    if (navigated) {
      onClose();
    }
  };

  const modalHeader = (
    <HStack className={modalStyles.modalHeaderContainer}>
      <Text className={modalStyles.modalHeaderTitle}>
        {tProfile("headerAvatar.switchProfileTitle")}
      </Text>
      <Button variant="ghost" onPress={onClose}>
        <Icon name="close" color={theme.colors.primary[50]} size={20} />
      </Button>
    </HStack>
  );

  const modalBody = (
    <VStack gap="md" p="l">
      {isLoadingAccounts ? (
        <Shimmer />
      ) : accounts.length === 0 ? (
        <Text>{tProfile("headerAvatar.switchProfileDescription")}</Text>
      ) : (
        <VStack gap={5} className={styles.formVStack}>
          <HStack gap={4} className={modalStyles.cardLayout}>
            {accountCards}
            {showAddAccount && (
              <HStack className={styles.cardWrapper}>
                <Card
                  key={`add-profile-card-modal-${accounts.length}`}
                  className={styles.addCardContainer}
                  onPress={handleAddProfilePress}
                >
                  <VStack
                    alignItems="center"
                    justify="center"
                    className={styles.addCardInner}
                  >
                    <Icon name="add" size={30} />
                    <Text className={styles.addText}>
                      {tAuth("multipleAccounts.add_profile")}
                    </Text>
                  </VStack>
                </Card>
              </HStack>
            )}
          </HStack>
        </VStack>
      )}
    </VStack>
  );

  const isDisabled =
    !selectedAccount ||
    isProceeding ||
    isLoadingAccounts ||
    accounts.length === 0 ||
    currentlyLoggedInAccount === selectedAccount?.unified_account_id;

  const modalFooter = (
    <STACK
      className={modalStyles.modalFooter}
      flexDirection={isNarrowLayout ? "column" : "row-reverse"}
    >
      <Button
        className={`${isDisabled ? modalStyles.disabledButton : modalStyles.saveButton}`}
        disabled={isDisabled}
        style={{}}
        onPress={handleContinue}
      >
        <Text
          className={
            isDisabled
              ? modalStyles.disabledButtonText
              : modalStyles.saveButtonText
          }
        >
          {tAuth("multipleAccounts.switchProfile")}
        </Text>
      </Button>
      {!isNarrowLayout && (
        <Button
          variant="ghost"
          onPress={onClose}
          className={modalStyles.cancelButton}
          style={{}}
        >
          <Text className={modalStyles.cancelButtonText}>
            {tProfile("product.cancel")}
          </Text>
        </Button>
      )}
    </STACK>
  );

  return (
    <>
      <Modal
        isModalOpen={isOpen}
        onClose={onClose}
        customHeader={modalHeader}
        body={modalBody}
        customFooter={modalFooter}
        showHeader
        showFooter
        overLayColor={theme.surface.overlay}
        width={isMobile ? "100%" : 560}
        height="auto"
        styles={{
          modal: {
            borderTopLeftRadius: isMobile ? "2.5rem" : "1.5rem",
            borderTopRightRadius: isMobile ? "2.5rem" : "1.5rem",
            borderBottomLeftRadius: isMobile ? undefined : "1.5rem",
            borderBottomRightRadius: isMobile ? undefined : "1.5rem",
          },
        }}
      />
      <NewProfileModal
        isOpen={isNewProfileModalOpen}
        onClose={() => setIsNewProfileModalOpen(false)}
        onContinue={handleNewProfileContinue}
      />
      <MessageModal
        isModalOpen={showContactSupportModal}
        setIsModalOpen={setShowContactSupportModal}
        onClose={() => {
          void (async () => {
            await handleContactSupportConfirm();
            if (loginMethod !== "phone_otp") logout();
          })();
        }}
        title={tProfile("product.noPrograms.title")}
        subtitle={tProfile("product.noPrograms.subtitle")}
        buttonText={tProfile("product.noPrograms.button")}
      />
    </>
  );
}
